/**
 * 从粘贴板 / 拖拽事件中提取图片 File 对象。
 * <p>
 * 浏览器 Web API 返回的是异构数据：
 * <ul>
 *   <li>ClipboardEvent.clipboardData.items：DataTransferItemList，图片 kind=file，需 getAsFile()</li>
 *   <li>ClipboardEvent.clipboardData.files：FileList，直接过滤 image/*</li>
 *   <li>DragEvent.dataTransfer.files：FileList，直接过滤 image/*</li>
 *   <li>DragEvent.dataTransfer.items：DataTransferItemList，同上</li>
 * </ul>
 * 某些浏览器（如 Firefox）在 paste 时不填 files，要从 items 读。
 * 某些拖拽只有 files 没有 items。两个来源都遍历一遍，去重。
 */

const IMAGE_MIME_PREFIX = 'image/'

function uniqueFiles(files: File[]): File[] {
	const seen = new Set<string>()
	const result: File[] = []
	for (const f of files) {
		const key = `${f.name}|${f.size}|${f.type}|${f.lastModified}`
		if (seen.has(key)) continue
		seen.add(key)
		result.push(f)
	}
	return result
}

function isImageFile(f: File): boolean {
	return f.type.startsWith(IMAGE_MIME_PREFIX)
}

/**
 * 从 ClipboardEvent 提取图片 File 数组。
 * 非图片 / 空剪贴板 / 浏览器不支持 → 返回空数组。
 */
export function extractImageFilesFromClipboard(
	event: ClipboardEvent,
): File[] {
	const cb = event.clipboardData
	if (!cb) return []
	const collected: File[] = []

	// 优先读 items（对截图粘贴更可靠）
	if (cb.items && cb.items.length > 0) {
		for (const item of Array.from(cb.items)) {
			if (item.kind === 'file' && isImageFileKind(item)) {
				const f = item.getAsFile()
				if (f && isImageFile(f)) collected.push(f)
			}
		}
	}

	// 某些浏览器（Firefox）items 里没有，但 files 里有
	if (collected.length === 0 && cb.files && cb.files.length > 0) {
		for (const f of Array.from(cb.files)) {
			if (isImageFile(f)) collected.push(f)
		}
	}

	return uniqueFiles(collected)
}

function isImageFileKind(item: DataTransferItem): boolean {
	const t = (item.type || '').toLowerCase()
	return t.startsWith(IMAGE_MIME_PREFIX) || t === ''
}

/**
 * 从 DragEvent 提取图片 File 数组。
 * 非图片拖拽 / 空 → 返回空数组。
 */
export function extractImageFilesFromDragEvent(
	event: DragEvent,
): File[] {
	const dt = event.dataTransfer
	if (!dt) return []
	const collected: File[] = []

	if (dt.items && dt.items.length > 0) {
		for (const item of Array.from(dt.items)) {
			if (item.kind === 'file' && isImageFileKind(item)) {
				const f = item.getAsFile()
				if (f && isImageFile(f)) collected.push(f)
			}
		}
	}

	if (collected.length === 0 && dt.files && dt.files.length > 0) {
		for (const f of Array.from(dt.files)) {
			if (isImageFile(f)) collected.push(f)
		}
	}

	return uniqueFiles(collected)
}
